/**
 * AtlasIdentityModule — the Atlas Passport and THE LOGIN (2026-10-05, spec docs/superpowers/specs/2026-10-05-atlas-login-design.md).
 *
 * The client no longer connects anonymously: `ensureToken()` resolves once a session token exists (a saved one, refreshed
 * in its last 7 days; or one from Sign in / Create account / Forgot password). On connect the Account row is the source of
 * truth (`atlas:account-ready`, as before). An unclaimed account gets a non-blocking "Secure your account" banner; a
 * connection the server REFUSES (`auth:*`) routes to the blocking claim screen (unclaimed) or back to Sign in.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { DbConnection } from '../module_bindings/index.js';
import { authApi, messageFor, tokenExpired, tokenNeedsRefresh } from '../lib/auth_client.js';
import { BANNER_LATER_KEY, dayStamp, laterHoldsToday } from '../lib/auth_banner_later.js';
import { getToken, setToken, clearToken, authStatus } from '../lib/auth_store.js';
import { tokenAtConnect } from '../net/connection.js';

type Mode = 'signin' | 'register' | 'forgot' | 'claim' | 'codes' | 'change' | 'regen';
/** A confirmation that must outlive the reload a password change makes (sessionStorage: this tab only). */
const AFTER_RELOAD_TOAST = 'atlas:auth-toast';
const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

export class AtlasIdentityModule implements IModule {
  readonly name = 'AtlasIdentity';

  private overlay!: HTMLElement;
  private form!: HTMLFormElement;
  private errEl!: HTMLElement;
  private noticeEl!: HTMLElement;
  private banner: HTMLElement | null = null;
  private mode: Mode = 'signin';
  private blocking = true;
  private proceeded = false;
  private insertWatcherBound = false;
  private refusing = false;
  private tokenWaiters: Array<(t: string) => void> = [];
  /** After the codes screen: continue with this token (first entry) or reload (an in-game claim). */
  private afterCodes: (() => void) | null = null;

  init(_app: App): void {
    this.overlay = document.getElementById('login')!;
    this.form = document.getElementById('login-form') as HTMLFormElement;
    this.errEl = document.getElementById('login-err')!;
    this.noticeEl = document.getElementById('login-notice')!;
    this.form.addEventListener('submit', (e) => { e.preventDefault(); void this.submit(); });
    bus.on('atlas:auth-open', ({ mode }) => this.openMode(mode));
    // The server refused a guarded reducer on this connection's session: it was ended elsewhere (a password change or
    // reset, or "sign out everywhere", moves the epoch). Keep playing on it and every trade fails; Sign in reloads onto
    // a fresh session.
    bus.on('atlas:session-ended', () => {
      if (this.overlay.classList.contains('show') && this.blocking) return;
      // Another tab (or this one, mid-reload) already stored a NEWER session: use it rather than wipe it.
      const stored = getToken();
      if (stored && stored !== tokenAtConnect()) { location.reload(); return; }
      clearToken();
      this.show('signin', true, 'Your sign-in on this device has ended (the password was changed or reset, or every device was signed out). Sign in again.');
    });
    // G1: Esc closes a NON-blocking account form (Sign in / the blocking claim screen stay up). Never the codes screen —
    // closing it would lose the codes. The Options handler yields to `#login.show`, so this press is the form's alone.
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || e.repeat || this.blocking || this.mode === 'codes' || !this.overlay.classList.contains('show')) return;
      e.preventDefault();
      this.hide();
    });
    // After the reload an in-game password change makes, the toast waits for the world (the HUD that shows it is a
    // world module, initialised after account-ready).
    bus.on('atlas:loading-done', () => {
      let text: string | null = null;
      try { text = sessionStorage.getItem(AFTER_RELOAD_TOAST); sessionStorage.removeItem(AFTER_RELOAD_TOAST); } catch { /* blocked storage */ }
      if (text) bus.emit('atlas:toast', { text, kind: 'success' });
    });
    // Probe lever: `ready()` is true once the Account row arrived (atlas:account-ready) — the overlay hiding is not proof.
    (window as unknown as { __login?: unknown }).__login = {
      mode: () => this.mode, shown: () => this.overlay.classList.contains('show'), ready: () => this.proceeded, status: () => ({ ...authStatus }),
    };
  }

  /** Resolves with a session token; shows Sign in when there is none. */
  async ensureToken(): Promise<string> {
    let t = getToken();
    if (t && tokenExpired(t)) { clearToken(); t = null; }
    if (t && tokenNeedsRefresh(t)) {
      const r = await authApi.refresh(t);
      if (r.ok) { setToken(r.data.token); t = r.data.token; }
    }
    if (t) return t;
    this.show('signin', true);
    return new Promise((resolve) => this.tokenWaiters.push(resolve));
  }

  onConnected(conn: DbConnection, identityHex: string): void {
    atlas.identityHex = identityHex;
    this.proceeded = false;
    if (!this.insertWatcherBound) {
      this.insertWatcherBound = true;
      conn.db.account.onInsert((_ctx, row) => {
        if (conn.identity && row.identity.isEqual(conn.identity)) this.proceed(row);
      });
    }
    const existing = conn.db.account.identity.find(conn.identity!);
    if (existing) { this.proceed(existing); void this.checkClaim(); return; }
    // A token whose identity has no account (the old kiosk state): sign in or create an account instead.
    clearToken();
    this.show('register', true);
  }

  /** The server refused the connection (or it died before onConnect). The SDK does not say why, so ask /auth/status. */
  async onConnectRefused(_err: Error): Promise<void> {
    if (this.refusing) return; // onConnectError and onDisconnect can both fire for one failure
    this.refusing = true;
    try {
      const t = getToken();
      if (!t) { this.show('signin', true); return; }
      const st = await authApi.status(t);
      if (!st.ok) {
        if (st.error === 'invalid_token') { clearToken(); this.show('signin', true, messageFor('invalid_token')); return; }
        this.show('signin', true, st.error === 'network' ? messageFor('network') : st.message); // keep the token: an outage is not a logout
        return;
      }
      if (st.data.hasAccount && !st.data.claimed && st.data.gateRefusesUnclaimed) { authStatus.username = st.data.username; this.show('claim', true); return; }
      if (st.data.claimed && st.data.session !== 'current') { clearToken(); this.show('signin', true, messageFor('invalid_token')); return; }
      this.show('signin', true, messageFor('refused_transient'));
    } finally { this.refusing = false; }
  }

  openMode(mode: 'claim' | 'change' | 'regen'): void { this.show(mode, false); }

  private async checkClaim(): Promise<void> {
    const t = getToken();
    if (!t) return;
    const st = await authApi.status(t);
    if (!st.ok) return;
    authStatus.claimed = st.data.claimed; authStatus.codesLeft = st.data.codesLeft; authStatus.username = st.data.username;
    const automation = typeof navigator !== 'undefined' && (navigator as Navigator & { webdriver?: boolean }).webdriver === true;
    let forced = false;
    try { forced = localStorage.getItem('atlas:auth-banner') === 'on'; } catch { /* fine */ }
    let laterToday = false;
    try { laterToday = laterHoldsToday(localStorage.getItem(BANNER_LATER_KEY), new Date()); } catch { /* storage denied: show it */ }
    if (!st.data.claimed && (!automation || forced) && !laterToday) this.showBanner(); // G9: "Later" holds for the day
  }

  private showBanner(): void {
    if (!this.banner) {
      this.banner = document.createElement('div');
      this.banner.id = 'auth-banner';
      this.banner.innerHTML = '<span>Secure your account — set a password so you can sign in from any device.</span><button type="button" data-k="auth:secure">Set a password</button><button type="button" class="later" data-k="auth:later">Later</button>';
      this.banner.querySelector('[data-k="auth:secure"]')!.addEventListener('click', () => { this.hideBanner(); this.show('claim', false); });
      this.banner.querySelector('[data-k="auth:later"]')!.addEventListener('click', () => {
        try { localStorage.setItem(BANNER_LATER_KEY, dayStamp(new Date())); } catch { /* storage denied */ }
        this.hideBanner();
      });
      document.body.appendChild(this.banner);
    }
    this.banner.classList.add('show');
  }
  private hideBanner(): void { this.banner?.classList.remove('show'); }

  private proceed(row: { username: string; displayName: string }): void {
    if (this.proceeded) return;
    this.proceeded = true;
    atlas.hasAccount = true;
    atlas.username = row.username;
    atlas.displayName = row.displayName;
    if (this.mode !== 'codes') this.hide();
    bus.emit('atlas:account-ready', { identityHex: atlas.identityHex!, username: row.username, displayName: row.displayName });
  }

  private show(mode: Mode, blocking: boolean, notice = ''): void {
    this.mode = mode; this.blocking = blocking;
    this.render(notice);
    this.overlay.classList.add('show');
    (this.form.querySelector('input') as HTMLInputElement | null)?.focus();
  }
  private hide(): void { this.overlay.classList.remove('show'); }

  private field(id: string, label: string, type: 'text' | 'password', autocomplete: string, max = 128): string {
    return `<label for="${id}">${esc(label)}</label><input id="${id}" type="${type}" autocomplete="${autocomplete}" spellcheck="false" maxlength="${max}" />`;
  }

  private render(notice: string): void {
    const title = document.getElementById('login-title')!;
    const lede = document.getElementById('login-lede')!;
    const links = document.getElementById('login-links')!;
    this.errEl.textContent = '';
    this.noticeEl.textContent = notice;
    const link = (k: string, text: string): string => `<button type="button" data-k="auth:${k}">${esc(text)}</button>`;
    let body = '', foot = '';
    switch (this.mode) {
      case 'signin':
        title.textContent = 'Sign in to Cosmos'; lede.textContent = 'Your passport works in every world, on any device.';
        body = this.field('login-username', 'Username', 'text', 'username', 20) + this.field('login-password', 'Password', 'password', 'current-password') + '<button class="primary" id="login-submit" type="submit">Sign in</button>';
        foot = link('to-register', 'Create an account') + link('to-forgot', 'Forgot password?');
        break;
      case 'register':
        title.textContent = 'Create your passport'; lede.textContent = 'Your username is permanent. You can change your display name later.';
        body = this.field('login-username', 'Username (3–20: letters, digits, _)', 'text', 'username', 20) + this.field('login-display', 'Display name', 'text', 'nickname', 32)
          + this.field('login-password', 'Password (at least 8 characters)', 'password', 'new-password') + this.field('login-password2', 'Password again', 'password', 'new-password')
          + '<button class="primary" id="login-submit" type="submit">Create account</button>';
        foot = link('to-signin', 'I already have an account');
        break;
      case 'forgot':
        title.textContent = 'Reset your password'; lede.textContent = 'Use one of the recovery codes you saved when you set your password.';
        body = this.field('login-username', 'Username', 'text', 'username', 20) + this.field('login-code', 'Recovery code', 'text', 'off', 16)
          + this.field('login-password', 'New password', 'password', 'new-password') + this.field('login-password2', 'New password again', 'password', 'new-password')
          + '<button class="primary" id="login-submit" type="submit">Reset password</button>';
        foot = link('to-signin', 'Back to sign in');
        break;
      case 'claim':
        title.textContent = 'Secure your account';
        lede.textContent = `Choose a password for ${authStatus.username ?? atlas.username ?? 'your traveler'}. You will sign in with it from any device — same traveler, nothing lost.`;
        body = this.field('login-password', 'Password (at least 8 characters)', 'password', 'new-password') + this.field('login-password2', 'Password again', 'password', 'new-password')
          + '<button class="primary" id="login-submit" type="submit">Set password</button>';
        foot = this.blocking ? link('switch-user', 'Sign in as someone else') : link('close', 'Later');
        break;
      case 'change':
        title.textContent = 'Change password'; lede.textContent = 'Every other device will be signed out.';
        body = this.field('login-current', 'Current password', 'password', 'current-password') + this.field('login-password', 'New password', 'password', 'new-password')
          + this.field('login-password2', 'New password again', 'password', 'new-password') + '<button class="primary" id="login-submit" type="submit">Change password</button>';
        foot = link('close', 'Cancel');
        break;
      case 'regen':
        title.textContent = 'New recovery codes'; lede.textContent = 'Your old codes stop working. Enter your password to continue.';
        body = this.field('login-current', 'Password', 'password', 'current-password') + '<button class="primary" id="login-submit" type="submit">Make new codes</button>';
        foot = link('close', 'Cancel');
        break;
      case 'codes':
        break; // rendered by showCodes()
    }
    this.form.innerHTML = body;
    links.innerHTML = foot;
    links.querySelectorAll<HTMLButtonElement>('button[data-k]').forEach((b) => b.addEventListener('click', () => {
      const k = b.dataset.k!.slice(5);
      if (k === 'close') this.hide();
      else if (k === 'switch-user') this.confirmSwitch();
      else this.show(k.slice(3) as Mode, this.blocking);
    }));
  }

  /** F1: the blocking claim screen's "Sign in as someone else" would orphan an UNCLAIMED traveler (no password to sign back in
   *  with) — say so, and offer the password first. */
  private confirmSwitch(): void {
    this.noticeEl.textContent = 'This traveler has no password. If you sign in as someone else you cannot come back to it — set a password first.';
    const links = document.getElementById('login-links')!;
    links.innerHTML = '<button type="button" data-k="auth:keep">Set a password first</button><button type="button" data-k="auth:switch-anyway">Sign in as someone else anyway</button>';
    links.querySelector('[data-k="auth:keep"]')!.addEventListener('click', () => { this.render(''); (this.form.querySelector('input') as HTMLInputElement | null)?.focus(); });
    links.querySelector('[data-k="auth:switch-anyway"]')!.addEventListener('click', () => { clearToken(); this.show('signin', true); });
  }

  private showCodes(codes: string[], then: () => void): void {
    this.mode = 'codes';
    this.afterCodes = then;
    document.getElementById('login-title')!.textContent = 'Save your recovery codes';
    document.getElementById('login-lede')!.textContent = 'Each code resets your password once. Keep them somewhere safe — they will not be shown again.';
    this.noticeEl.textContent = ''; this.errEl.textContent = '';
    this.form.innerHTML = `<div class="codes" id="login-codes">${codes.map((c) => `<span>${esc(c)}</span>`).join('')}</div>
      <button type="button" class="primary" id="login-copy" style="background:var(--ax-bg3);color:var(--ax-text)">Copy codes</button>
      <label class="ack"><input type="checkbox" id="login-codes-ack" /> I saved these codes</label>
      <button class="primary" id="login-submit" type="submit" disabled>Continue</button>`;
    document.getElementById('login-links')!.innerHTML = '';
    const ack = document.getElementById('login-codes-ack') as HTMLInputElement;
    const go = document.getElementById('login-submit') as HTMLButtonElement;
    ack.addEventListener('change', () => { go.disabled = !ack.checked; });
    document.getElementById('login-copy')!.addEventListener('click', () => { try { void navigator.clipboard.writeText(codes.join('\n')); } catch { /* blocked */ } });
    this.overlay.classList.add('show');
  }

  private val(id: string): string { return (document.getElementById(id) as HTMLInputElement | null)?.value ?? ''; }
  private fail(message: string): void { this.errEl.textContent = message; const b = document.getElementById('login-submit') as HTMLButtonElement | null; if (b) b.disabled = false; }

  /** A new token: the first entry resolves the waiting connection; an in-game change reloads (RECONNECT = RELOAD + RESUME). */
  private useToken(token: string): void {
    // Never a non-string (a bad 2xx once stored "undefined" as the session — final review F7).
    if (typeof token !== 'string' || !token) { this.fail(messageFor('unavailable')); return; }
    setToken(token);
    const waiters = this.tokenWaiters.splice(0);
    if (waiters.length) { this.hide(); for (const w of waiters) w(token); }
    else location.reload();
  }

  private async submit(): Promise<void> {
    if (this.mode === 'codes') { const then = this.afterCodes; this.afterCodes = null; this.hide(); then?.(); return; }
    const btn = document.getElementById('login-submit') as HTMLButtonElement | null;
    if (btn) btn.disabled = true;
    this.errEl.textContent = '';
    const pw = this.val('login-password'), pw2 = this.val('login-password2');
    const needsPair = this.mode === 'register' || this.mode === 'forgot' || this.mode === 'claim' || this.mode === 'change';
    if (needsPair && pw !== pw2) { this.fail('The two passwords do not match.'); return; }
    switch (this.mode) {
      case 'signin': {
        const r = await authApi.login(this.val('login-username').trim(), pw);
        if (!r.ok) { this.fail(r.message); return; }
        this.useToken(r.data.token); return;
      }
      case 'register': {
        const u = this.val('login-username').trim();
        if (!/^[A-Za-z0-9_]{3,20}$/.test(u)) { this.fail('Username must be 3–20 characters: letters, digits, underscore.'); return; }
        const r = await authApi.register(u, this.val('login-display').trim(), pw);
        if (!r.ok) { this.fail(r.message); return; }
        this.showCodes(r.data.recoveryCodes, () => this.useToken(r.data.token)); return;
      }
      case 'forgot': {
        const r = await authApi.recover(this.val('login-username').trim(), this.val('login-code'), pw);
        if (!r.ok) { this.fail(r.message); return; }
        this.useToken(r.data.token); return;
      }
      case 'claim': {
        const t = getToken();
        if (!t) { this.show('signin', true); return; }
        const r = await authApi.claim(t, pw);
        if (!r.ok) { this.fail(r.message); return; }
        authStatus.claimed = true;
        this.showCodes(r.data.recoveryCodes, () => this.useToken(r.data.token)); return;
      }
      case 'change': {
        const t = getToken();
        const r = t ? await authApi.changePassword(t, this.val('login-current'), pw) : null;
        if (!r || !r.ok) { this.fail(r ? r.message : messageFor('invalid_token')); return; }
        // Reload like a claim: every module's atlas.conn still holds the OLD epoch's token, refused from now on (a claimed
        // account is always session-checked), so their HTTP SQL calls would fail (final review M3). The toast follows.
        try { sessionStorage.setItem(AFTER_RELOAD_TOAST, 'Password changed — other devices were signed out.'); } catch { /* the toast is a nicety */ }
        this.useToken(r.data.token); return;
      }
      case 'regen': {
        const t = getToken();
        const r = t ? await authApi.recoveryCodes(t, this.val('login-current')) : null;
        if (!r || !r.ok) { this.fail(r ? r.message : messageFor('invalid_token')); return; }
        authStatus.codesLeft = r.data.recoveryCodes.length;
        this.showCodes(r.data.recoveryCodes, () => this.hide()); return;
      }
    }
  }
}
